import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { enableMapSet } from 'immer';

// Enable Immer support for Map/Set mutations used throughout the scenario store
enableMapSet();
import type {
    Scenario,
    ScenarioWithResults,
    CreateScenarioInput,
    UpdateScenarioInput,
    SimulationResultPayload,
    SimulationGraph,
    SimulationRunConfig,
    ScenarioValidationError,
    ScenarioDistributionOverrides,
    DistributionNodeData,
} from '../models/simulation';
import * as scenarioService from '../services/scenarioService';
import { errorService } from '../services/errorService';

const BASELINE_DISPLAY_NAME = 'Baseline Scenario';
const LEGACY_BASELINE_NAMES = new Set(['baseline', 'baseline scenario', 'main', 'main simulation']);
const normalizeScenarioName = (name: string) => name.trim().toLowerCase();
const isLegacyBaselineName = (name: string) => LEGACY_BASELINE_NAMES.has(normalizeScenarioName(name));
const isScenarioBaseline = (scenario: Scenario) =>
    scenario.isBaseline || isLegacyBaselineName(scenario.name);
const findBaselineScenario = (scenarios: Scenario[]) =>
    scenarios.find((scenario) => isScenarioBaseline(scenario));

// Helper function to apply scenario overrides to a graph
export const applyScenarioOverrides = (
    graph: SimulationGraph,
    overrides: ScenarioDistributionOverrides
): SimulationGraph => {
    const graphCopy = JSON.parse(JSON.stringify(graph)) as SimulationGraph;

    // Apply overrides to distribution nodes
    Object.entries(overrides).forEach(([nodeId, config]) => {
        const node = graphCopy.nodes.find((n) => n.id === nodeId);
        if (node && node.data.type === 'distribution') {
            node.data.config = config;
        }
    });

    return graphCopy;
};

interface ScenarioState {
    // Data
    scenarios: Scenario[];
    scenarioResults: Map<string, SimulationResultPayload>;
    activeScenarioId: string | null;
    currentSimulationId: string | null;

    // Loading states
    isLoading: boolean;
    isSaving: boolean;
    isRunning: Map<string, boolean>; // scenarioId -> isRunning
    scenarioProgress: Map<string, { completedTrials: number; totalTrials: number }>; // scenarioId -> progress

    // Errors
    loadError: string | null;
    saveError: string | null;
    validationErrors: Map<string, ScenarioValidationError[]>; // scenarioId -> errors

    // Actions - Initialization
    initializeForSimulation: (
        simulationId: string,
        graph: SimulationGraph,
        options?: { canMutate?: boolean }
    ) => Promise<void>;
    clearScenarios: () => void;

    // Actions - Scenario Management
    loadScenarios: (simulationId: string) => Promise<void>;
    loadScenarioResults: (scenarioId: string) => Promise<void>;
    createScenario: (input: CreateScenarioInput, autoRun?: boolean) => Promise<Scenario | null>;
    updateScenario: (scenarioId: string, input: UpdateScenarioInput) => Promise<void>;
    deleteScenario: (scenarioId: string) => Promise<void>;
    setActiveScenario: (scenarioId: string | null) => void;

    // Actions - Scenario Execution
    runScenario: (scenarioId: string, graph: SimulationGraph, runConfig: SimulationRunConfig, runFn: (graph: SimulationGraph, config: SimulationRunConfig, onProgress?: (completedTrials: number, totalTrials: number, partialResults: Record<string, any>) => void) => Promise<SimulationResultPayload>) => Promise<void>;
    setScenarioProgress: (scenarioId: string, completedTrials: number, totalTrials: number) => void;
    clearScenarioProgress: (scenarioId: string) => void;
    updateScenarioDistribution: (scenarioId: string, nodeId: string, config: DistributionNodeData['config'], graph: SimulationGraph, runConfig: SimulationRunConfig, runFn: (graph: SimulationGraph, config: SimulationRunConfig) => Promise<SimulationResultPayload>) => Promise<void>;
    resetScenarioDistribution: (scenarioId: string, nodeId: string, graph: SimulationGraph, runConfig: SimulationRunConfig, runFn: (graph: SimulationGraph, config: SimulationRunConfig) => Promise<SimulationResultPayload>) => Promise<void>;

    // Actions - Validation
    validateScenario: (scenarioId: string, graph: SimulationGraph) => ScenarioValidationError[];
    validateAllScenarios: (graph: SimulationGraph) => void;
    markScenarioStale: (scenarioId: string) => void;
    updateScenarioGraphTimestamp: (scenarioId: string, graphDataUpdatedAt: string) => Promise<void>;
    syncBaselineScenario: (graph: SimulationGraph) => Promise<void>;

    // Helpers
    getScenario: (scenarioId: string) => Scenario | undefined;
    getScenarioResults: (scenarioId: string) => SimulationResultPayload | undefined;
    getBaselineScenario: () => Scenario | undefined;
    isScenarioStale: (scenarioId: string, currentGraphDataUpdatedAt: string) => boolean;
    getNextScenarioName: () => string;
}

export const useScenarioStore = create<ScenarioState>()(
    immer((set, get) => ({
        // Initial state
        scenarios: [],
        scenarioResults: new Map(),
        activeScenarioId: null,
        currentSimulationId: null,
        isLoading: false,
        isSaving: false,
        isRunning: new Map(),
        scenarioProgress: new Map(),
        loadError: null,
        saveError: null,
        validationErrors: new Map(),

        // Initialize for a simulation
        initializeForSimulation: async (simulationId: string, graph: SimulationGraph, options = {}) => {
            const canMutate = options.canMutate ?? true;

            set((state) => {
                state.currentSimulationId = simulationId;
                state.isLoading = true;
                state.loadError = null;
            });

            try {
                await get().loadScenarios(simulationId);

                const hasGraphStructure = graph.nodes.length > 0;

                if (canMutate && hasGraphStructure) {
                    // Check if baseline scenario exists (flag or legacy name)
                    let baselineScenario = get().getBaselineScenario();

                    // If we found a legacy baseline (name and/or flag), normalize it.
                    if (baselineScenario && !baselineScenario.isBaseline) {
                        await get().updateScenario(baselineScenario.id, {
                            name: BASELINE_DISPLAY_NAME,
                            isBaseline: true,
                            distributionOverrides: {},
                            graphDataUpdatedAt: graph.dataUpdatedAt,
                        });
                        baselineScenario = get().getBaselineScenario();
                    } else if (baselineScenario && baselineScenario.name !== BASELINE_DISPLAY_NAME) {
                        await get().updateScenario(baselineScenario.id, {
                            name: BASELINE_DISPLAY_NAME,
                        });
                        baselineScenario = get().getBaselineScenario();
                    }

                    if (!baselineScenario) {
                        // Create baseline scenario
                        await get().createScenario({
                            simulationId,
                            name: BASELINE_DISPLAY_NAME,
                            description: 'Default scenario using graph distributions',
                            distributionOverrides: {},
                            graphDataUpdatedAt: graph.dataUpdatedAt,
                            isBaseline: true,
                        }, true);
                    }
                }

                // Load results for all scenarios from the database
                const scenarios = get().scenarios;
                if (scenarios.length > 0) {
                    const scenarioIds = scenarios.map(s => s.id);
                    const scenariosWithResults = await scenarioService.loadScenariosWithResults(scenarioIds);

                    // Store results in the map
                    set((state) => {
                        scenariosWithResults.forEach((scenario) => {
                            if (scenario.results) {
                                state.scenarioResults.set(scenario.id, scenario.results);
                            }
                        });
                    });
                }

                // Set baseline as active if no active scenario
                if (!get().activeScenarioId) {
                    const baseline = get().getBaselineScenario();
                    const fallbackScenario = baseline ?? get().scenarios[0];
                    if (fallbackScenario) {
                        set((state) => {
                            state.activeScenarioId = fallbackScenario.id;
                        });
                    }
                }
            } catch (error) {
                errorService.logError('Failed to initialize scenarios', error, {
                    action: 'initializeForSimulation',
                    metadata: { simulationId },
                });
                set((state) => {
                    state.loadError = error instanceof Error ? error.message : 'Failed to initialize scenarios';
                });
            } finally {
                set((state) => {
                    state.isLoading = false;
                });
            }
        },

        clearScenarios: () => {
            set((state) => {
                state.scenarios = [];
                state.scenarioResults = new Map();
                state.activeScenarioId = null;
                state.currentSimulationId = null;
                state.isLoading = false;
                state.isSaving = false;
                state.isRunning = new Map();
                state.scenarioProgress = new Map();
                state.loadError = null;
                state.saveError = null;
                state.validationErrors = new Map();
            });
        },

        loadScenarios: async (simulationId: string) => {
            set((state) => {
                state.isLoading = true;
                state.loadError = null;
            });

            try {
                const scenarios = await scenarioService.loadScenarios(simulationId);
                set((state) => {
                    state.scenarios = scenarios;
                    state.currentSimulationId = simulationId;
                });
            } catch (error) {
                errorService.logError('Failed to load scenarios', error, {
                    action: 'loadScenarios',
                    metadata: { simulationId },
                });
                set((state) => {
                    state.loadError = error instanceof Error ? error.message : 'Failed to load scenarios';
                });
            } finally {
                set((state) => {
                    state.isLoading = false;
                });
            }
        },

        loadScenarioResults: async (scenarioId: string) => {
            try {
                const scenario = await scenarioService.loadScenarioWithResults(scenarioId);
                if (scenario?.results) {
                    set((state) => {
                        state.scenarioResults.set(scenarioId, scenario.results!);
                    });
                }
            } catch (error) {
                errorService.logError('Failed to load scenario results', error, {
                    action: 'loadScenarioResults',
                    metadata: { scenarioId },
                });
            }
        },

        createScenario: async (input: CreateScenarioInput, autoRun = false) => {
            // Prevent creating duplicate baseline scenarios
            if (input.isBaseline) {
                const existingBaseline = get().getBaselineScenario();
                if (existingBaseline) {
                    // Baseline already exists, return it instead of creating a duplicate
                    return existingBaseline;
                }
            }

            set((state) => {
                state.isSaving = true;
                state.saveError = null;
            });

            try {
                const scenario = await scenarioService.createScenario(input);
                set((state) => {
                    state.scenarios.push(scenario);
                    state.activeScenarioId = scenario.id;
                });

                // Auto-run if requested (typically for new scenarios)
                if (autoRun) {
                    // Note: Actual run will be triggered externally with the graph
                    // This just sets up the scenario to be ready
                }

                return scenario;
            } catch (error) {
                errorService.logError('Failed to create scenario', error, {
                    action: 'createScenario',
                    metadata: { input },
                });
                set((state) => {
                    state.saveError = error instanceof Error ? error.message : 'Failed to create scenario';
                });
                return null;
            } finally {
                set((state) => {
                    state.isSaving = false;
                });
            }
        },

        updateScenario: async (scenarioId: string, input: UpdateScenarioInput) => {
            set((state) => {
                state.isSaving = true;
                state.saveError = null;
            });

            try {
                const updatedScenario = await scenarioService.updateScenario(scenarioId, input);
                set((state) => {
                    const index = state.scenarios.findIndex((s) => s.id === scenarioId);
                    if (index !== -1) {
                        state.scenarios[index] = updatedScenario;
                    }
                });
            } catch (error) {
                errorService.logError('Failed to update scenario', error, {
                    action: 'updateScenario',
                    metadata: { scenarioId, input },
                });
                set((state) => {
                    state.saveError = error instanceof Error ? error.message : 'Failed to update scenario';
                });
            } finally {
                set((state) => {
                    state.isSaving = false;
                });
            }
        },

        deleteScenario: async (scenarioId: string) => {
            set((state) => {
                state.isSaving = true;
                state.saveError = null;
            });

            try {
                await scenarioService.deleteScenario(scenarioId);
                set((state) => {
                    state.scenarios = state.scenarios.filter((s) => s.id !== scenarioId);
                    state.scenarioResults.delete(scenarioId);
                    state.validationErrors.delete(scenarioId);
                    state.isRunning.delete(scenarioId);

                    // If deleted scenario was active, switch to baseline
                    if (state.activeScenarioId === scenarioId) {
                        const baseline = findBaselineScenario(state.scenarios);
                        state.activeScenarioId = baseline?.id || null;
                    }
                });
            } catch (error) {
                errorService.logError('Failed to delete scenario', error, {
                    action: 'deleteScenario',
                    metadata: { scenarioId },
                });
                set((state) => {
                    state.saveError = error instanceof Error ? error.message : 'Failed to delete scenario';
                });
            } finally {
                set((state) => {
                    state.isSaving = false;
                });
            }
        },

        setActiveScenario: (scenarioId: string | null) => {
            set((state) => {
                state.activeScenarioId = scenarioId;
            });
        },

        setScenarioProgress: (scenarioId: string, completedTrials: number, totalTrials: number) => {
            set((state) => {
                state.scenarioProgress.set(scenarioId, { completedTrials, totalTrials });
            });
        },
        clearScenarioProgress: (scenarioId: string) => {
            set((state) => {
                state.scenarioProgress.delete(scenarioId);
            });
        },
        runScenario: async (scenarioId: string, graph: SimulationGraph, runConfig: SimulationRunConfig, runFn: (graph: SimulationGraph, config: SimulationRunConfig, onProgress?: (completedTrials: number, totalTrials: number, partialResults: Record<string, any>) => void) => Promise<SimulationResultPayload>) => {
            const scenario = get().getScenario(scenarioId);
            if (!scenario) return;

            set((state) => {
                state.isRunning.set(scenarioId, true);
                state.scenarioProgress.delete(scenarioId); // Clear any previous progress
            });

            try {
                // Apply scenario overrides to graph
                const graphWithOverrides = applyScenarioOverrides(graph, scenario.distributionOverrides);

                // Create progress callback to update scenario store
                const onProgress = (completedTrials: number, totalTrials: number, partialResults: Record<string, any>) => {
                    // Update scenario progress
                    get().setScenarioProgress(scenarioId, completedTrials, totalTrials);

                    if (Object.keys(partialResults).length === 0) {
                        return;
                    }
                    
                    // Store partial results in scenario results map
                    set((state) => {
                        // Initialize or update partial result
                        if (!state.scenarioResults.has(scenarioId)) {
                            state.scenarioResults.set(scenarioId, {
                                graphId: graph.id,
                                results: partialResults,
                                runConfig,
                                durationMs: 0
                            });
                        } else {
                            // Merge partial results
                            const existing = state.scenarioResults.get(scenarioId)!;
                            Object.assign(existing.results, partialResults);
                        }
                    });
                };

                // Run simulation with progress callback
                const result = await runFn(graphWithOverrides, runConfig, onProgress);

                // Store final results
                set((state) => {
                    state.scenarioResults.set(scenarioId, result);
                });

                // Save results to backend
                await scenarioService.updateScenarioResults(scenarioId, result);
            } catch (error) {
                errorService.logError('Failed to run scenario', error, {
                    action: 'runScenario',
                    metadata: { scenarioId },
                });
            } finally {
                set((state) => {
                    state.isRunning.set(scenarioId, false);
                    state.scenarioProgress.delete(scenarioId);
                });
            }
        },

        updateScenarioDistribution: async (scenarioId: string, nodeId: string, config: DistributionNodeData['config'], graph: SimulationGraph, runConfig: SimulationRunConfig, runFn: (graph: SimulationGraph, config: SimulationRunConfig, onProgress?: (completedTrials: number, totalTrials: number, partialResults: Record<string, any>) => void) => Promise<SimulationResultPayload>) => {
            const scenario = get().getScenario(scenarioId);
            if (!scenario) return;

            const newOverrides = {
                ...scenario.distributionOverrides,
                [nodeId]: config,
            };

            await get().updateScenario(scenarioId, {
                distributionOverrides: newOverrides,
            });

            // Run simulation with updated overrides
            await get().runScenario(scenarioId, graph, runConfig, runFn);
        },

        resetScenarioDistribution: async (scenarioId: string, nodeId: string, graph: SimulationGraph, runConfig: SimulationRunConfig, runFn: (graph: SimulationGraph, config: SimulationRunConfig, onProgress?: (completedTrials: number, totalTrials: number, partialResults: Record<string, any>) => void) => Promise<SimulationResultPayload>) => {
            const scenario = get().getScenario(scenarioId);
            if (!scenario) return;

            const newOverrides = { ...scenario.distributionOverrides };
            delete newOverrides[nodeId];

            await get().updateScenario(scenarioId, {
                distributionOverrides: newOverrides,
            });

            // Run simulation with updated overrides
            await get().runScenario(scenarioId, graph, runConfig, runFn);
        },

        validateScenario: (scenarioId: string, graph: SimulationGraph): ScenarioValidationError[] => {
            const scenario = get().getScenario(scenarioId);
            if (!scenario) return [];

            const errors: ScenarioValidationError[] = [];
            const nodeMap = new Map(graph.nodes.map((node) => [node.id, node]));

            // Check each distribution override
            Object.keys(scenario.distributionOverrides).forEach((nodeId) => {
                const node = nodeMap.get(nodeId);

                if (!node) {
                    errors.push({
                        nodeId,
                        type: 'removed',
                        message: `Node has been removed from the graph`,
                    });
                } else if (node.data.type !== 'distribution') {
                    errors.push({
                        nodeId,
                        nodeName: node.data.name,
                        type: 'changed',
                        message: `Node is no longer a distribution node`,
                    });
                }
            });

            set((state) => {
                if (errors.length > 0) {
                    state.validationErrors.set(scenarioId, errors);
                } else {
                    state.validationErrors.delete(scenarioId);
                }
            });

            return errors;
        },

        validateAllScenarios: (graph: SimulationGraph) => {
            const scenarios = get().scenarios;
            scenarios.forEach((scenario) => {
                get().validateScenario(scenario.id, graph);
            });
        },

        markScenarioStale: (scenarioId: string) => {
            // Mark a scenario as needing revalidation
            // This is called when the graph changes
            set((state) => {
                const scenario = state.scenarios.find((s) => s.id === scenarioId);
                if (scenario) {
                    // Stale scenarios will be detected by comparing graphDataUpdatedAt
                }
            });
        },

        updateScenarioGraphTimestamp: async (scenarioId: string, graphDataUpdatedAt: string) => {
            await get().updateScenario(scenarioId, {
                graphDataUpdatedAt,
            });
        },

        getScenario: (scenarioId: string) => {
            return get().scenarios.find((s) => s.id === scenarioId);
        },

        getScenarioResults: (scenarioId: string) => {
            return get().scenarioResults.get(scenarioId);
        },

        getBaselineScenario: () => {
            return findBaselineScenario(get().scenarios) || undefined;
        },

        isScenarioStale: (scenarioId: string, currentGraphDataUpdatedAt: string) => {
            const scenario = get().getScenario(scenarioId);
            if (!scenario) return false;

            // Baseline is never stale (it auto-updates)
            if (isScenarioBaseline(scenario)) return false;

            return scenario.graphDataUpdatedAt !== currentGraphDataUpdatedAt;
        },

        getNextScenarioName: () => {
            const scenarios = get().scenarios;
            const scenarioNumbers = scenarios
                .map((s) => {
                    const match = s.name.match(/^Scenario (\d+)$/);
                    return match ? parseInt(match[1], 10) : 0;
                })
                .filter((n) => n > 0);

            const maxNumber = scenarioNumbers.length > 0 ? Math.max(...scenarioNumbers) : 0;
            return `Scenario ${maxNumber + 1}`;
        },

        syncBaselineScenario: async (graph: SimulationGraph) => {
            // Update baseline scenario to match current graph timestamp
            // This ensures baseline always uses the latest graph distributions
            if (graph.nodes.length === 0) {
                return;
            }

            const simulationId = get().currentSimulationId;
            if (!simulationId) {
                return;
            }

            // Reload scenarios first to ensure we have the latest state from the database
            // This prevents duplicate baseline creation if initializeForSimulation already created one
            await get().loadScenarios(simulationId);

            const baseline = get().getBaselineScenario();

            // If baseline doesn't exist, create it
            if (!baseline) {
                // Auto-generate baseline scenario
                await get().createScenario({
                    simulationId,
                    name: BASELINE_DISPLAY_NAME,
                    description: 'Default scenario using graph distributions',
                    distributionOverrides: {},
                    graphDataUpdatedAt: graph.dataUpdatedAt,
                    isBaseline: true,
                }, false);

                // Reload scenarios again to ensure we have the newly created baseline
                await get().loadScenarios(simulationId);

                // Set as active if no active scenario
                if (!get().activeScenarioId) {
                    const newBaseline = get().getBaselineScenario();
                    if (newBaseline) {
                        set((state) => {
                            state.activeScenarioId = newBaseline.id;
                        });
                    }
                }
                return;
            }

            // Check if baseline needs updating
            if (baseline.graphDataUpdatedAt !== graph.dataUpdatedAt) {
                await get().updateScenario(baseline.id, {
                    graphDataUpdatedAt: graph.dataUpdatedAt,
                    distributionOverrides: {}, // Baseline always has empty overrides
                });
            }
        },
    }))
);

// Selectors
export const useScenarios = () => useScenarioStore((state) => state.scenarios);
export const useActiveScenario = () => {
    const activeScenarioId = useScenarioStore((state) => state.activeScenarioId);
    const getScenario = useScenarioStore((state) => state.getScenario);
    return activeScenarioId ? getScenario(activeScenarioId) : undefined;
};
export const useScenarioResults = (scenarioId: string | null) => {
    const getScenarioResults = useScenarioStore((state) => state.getScenarioResults);
    return scenarioId ? getScenarioResults(scenarioId) : undefined;
};
export const useIsScenarioRunning = (scenarioId: string | null) => {
    const isRunning = useScenarioStore((state) => state.isRunning);
    return scenarioId ? isRunning.get(scenarioId) || false : false;
};
